import {analytics} from '@wonderlandengine/upsdk';
import {StatNames} from '../enums/StatNames.js';

import type {GameAnalytics as GA} from 'gameanalytics';
import {
    ErrorSeverity,
    ProgressionStatus,
    ResourceFlowType,
} from '../enums/game-analytics-enums.js';

declare var GameAnalytics: typeof GA;

export enum AnalyticsEvent {
    ShowMainMenu,
    ShowGameSetup,
    ShowPause,
    ShowGameOver,
    ShowOptions,
    ShowCredits,
    ShowCoinStore,

    TutorialStart,
    TutorialDone,

    FacebookLoggedIn,
    FacebookLoggedOut,

    EarnedCoins,
    SpentCoins,
    BoughtCoins,

    EnvironmentIsland,
    EnvironmentDesert,
    EnvironmentArcitc,
    EnvironmentSwamp,
    EnvironmentForest,

    ShowGame,
    ShowNormalMode,
    ShowTimedMode,
    ShowSurvivalMode,

    ShowInventoryStore,
    ShowUpgradeStore,
    ShowPerksStore,
    ShowStoreOverall,
    ShowMalwearStore,
    ShowAchievements,
    ShowStats,

    EnvironmentMoon,
    ShowNews,
    EnvironmentCandy,
    EnvironmentWuk,

    GameRank,
    ShowExpertMode,

    ClocksPerGame,
    SkullsPerGame,

    TotalBlobs,

    LevelReachedNormal,
    LevelReachedTimed,
    LevelReachedSkull,
    LevelReachedExpert,

    MissionCompleted,
    RankUp,

    ShapeBonusesPerGame,

    BombsUsed,
    FruitSmashersUsed,
    NukesUsed,
    SkullSmashersUsed,
    ClockMagnetsUsed,
    BarriersUsed,

    ScoreNormal,
    ScoreExpert,
    ScoreSkull,
    ScoreTimed,

    StarsPerGame,
    FruitLeftInQuickplay,

    ScoreQuickplay,
    ScoreFungus,
    ScoreCupcake,

    ShowMore,

    ShowModeSelect,
    ShowSpecialOffers,
    ShowLeaderboard,
    ShowLeaderboardStats,
    ShowLeaderboardInvite,

    LevelReachedFungus,
    LevelReachedCupcake,

    ShowLoadout1,
    ShowLoadout2,

    FreezeDropsUsed,
    //times beat
    // malwear
    // powerups equipped, used, upgraded
}
export class Analytics {
    static LogEvent(name: AnalyticsEvent) {
        GameAnalytics.addDesignEvent('custom:' + AnalyticsEvent[name].toLowerCase());
    }

    private static _valueIsEventEnum(val: any): val is AnalyticsEvent {
        // Check if val is a number and is a valid enum value
        if (typeof val === 'number') {
            return typeof AnalyticsEvent[val] === 'string';
        }

        // Check if val is a string and is a valid enum key
        if (typeof val === 'string') {
            return isNaN(Number(val)) && val in AnalyticsEvent;
        }

        return false;
    }

    static LogEventWithValue(
        name: AnalyticsEvent | string,
        val: number,
        itemId: string = ''
    ) {
        switch (name) {
            case AnalyticsEvent.BoughtCoins:
                GameAnalytics.addResourceEvent(
                    ResourceFlowType.Source,
                    'coins',
                    val,
                    'buy',
                    itemId
                );
                return;
            case AnalyticsEvent.EarnedCoins:
                GameAnalytics.addResourceEvent(
                    ResourceFlowType.Source,
                    'coins',
                    val,
                    'earn',
                    itemId
                );
                return;
            case AnalyticsEvent.SpentCoins:
                GameAnalytics.addResourceEvent(
                    ResourceFlowType.Sink,
                    'coins',
                    val,
                    'spend',
                    itemId
                );
                return;
            case AnalyticsEvent.ShowGameOver:
                return;
            case AnalyticsEvent.TutorialDone:
                GameAnalytics.addProgressionEvent(ProgressionStatus.Complete, 'tutorial');
                return;
            case AnalyticsEvent.ScoreNormal:
            case AnalyticsEvent.ScoreExpert:
            case AnalyticsEvent.ScoreSkull:
            case AnalyticsEvent.ScoreTimed:
            case AnalyticsEvent.ScoreQuickplay:
            case AnalyticsEvent.ScoreFungus:
            case AnalyticsEvent.ScoreCupcake:
                GameAnalytics.addProgressionEvent(
                    ProgressionStatus.Complete,
                    'game',
                    '',
                    '',
                    val
                );
            default:
                // is the name an enum?
                let eventname: string;
                if (this._valueIsEventEnum(name)) {
                    eventname = 'custom:' + AnalyticsEvent[name].toLowerCase();
                } else {
                    eventname = name;
                }

                GameAnalytics.addDesignEvent(eventname, val);
        }
    }

    static StartTimedEvent(name: AnalyticsEvent) {
        if (name === AnalyticsEvent.ShowGame) {
            GameAnalytics.addProgressionEvent(ProgressionStatus.Start, 'game');
            analytics.trackGameplayStart();
        }
    }

    static StopTimedEvent(name: AnalyticsEvent) {
        if (name === AnalyticsEvent.ShowGame) {
            analytics.trackGameplayStop();
        }
    }

    static LogUserID(userId: string) {
        // TODO
    }

    static LogUserGender(male: boolean) {
        // TODO
    }

    static LogError(message: string) {
        GameAnalytics.addErrorEvent(ErrorSeverity.Error, message);
    }

    static LogStatWithValue(name: StatNames, val: number) {
        GameAnalytics.addDesignEvent('stat:' + StatNames[name].toLowerCase(), val);
    }
}
